import { Injectable, Injector, inject, type AnimationCallbackEvent } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { PlatformCustomerService } from '../../../../core/services/platform-customer.service';
import { WorkspaceSwitcherDialogComponent } from './workspace-switcher-dialog.component';
import { WORKSPACE_SWITCHER_CLOSE } from './workspace-switcher-dialog.tokens';
import { enterMotion, isShowing, leaveMotion } from '../../../../core/motion';

const event = (target: Element, done: () => void = () => undefined): AnimationCallbackEvent =>
  ({ target, animationComplete: done }) as AnimationCallbackEvent;

/**
 * WorkspaceSwitcherDialogService — opens the searchable "Switch workspace"
 * dialog.
 *
 * RESPONSIBILITY: own the overlay the dialog lives in. The profile panel
 * opens it once the accessible list outgrows an inline list
 * (`shouldUseDialog`); everything inside is the component's.
 *
 * WHY AN OVERLAY. The profile panel is a glass surface that animates with
 * `transform`, and either property makes it the containing block for every
 * `position: fixed` descendant — a dialog rendered inside it would be
 * positioned against the panel, not the viewport. A GLOBAL CDK overlay is
 * attached to `body`, so the dialog is correct wherever the panel is (the
 * app shell and the admin shell both host one). Same shape as
 * `PostViewerService`.
 *
 * SIGNALS: none — the open state is the overlay's own `hasAttached()`.
 * USED BY: ProfilePanelComponent.
 * BACKEND CONTRACT: none directly; the dialog switches through
 * `PlatformCustomerService.switchTo`.
 * AI CONTEXT: close paths are the backdrop, Escape (`keydownEvents`) and the
 * dialog's own close button through `WORKSPACE_SWITCHER_CLOSE`. A successful
 * switch hard-reloads the app, so there is no "switched" close path.
 *
 * MOTION (docs/MOTION.md): the dialog pops out of `origin` (the avatar the
 * profile panel hung from) and recedes back into it, or where it stands when
 * that has gone; the backdrop is detached at once so it fades with CDK's own
 * transition while the panel recedes. Reduced motion: both instant.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceSwitcherDialogService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly analytics = inject(AnalyticsService);
  private readonly customers = inject(PlatformCustomerService);

  private overlayRef: OverlayRef | null = null;
  private origin: Element | null = null;

  open(origin: Element | null = null): void {
    if (this.overlayRef?.hasAttached()) return;

    const ref = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'ws-switcher__backdrop',
      panelClass: 'ws-switcher__pane',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().global().centerHorizontally().top('12vh'),
    });
    this.overlayRef = ref;

    const injector = Injector.create({
      parent: this.injector,
      providers: [{ provide: WORKSPACE_SWITCHER_CLOSE, useValue: () => this.close() }],
    });
    const attached = ref.attach(new ComponentPortal(WorkspaceSwitcherDialogComponent, null, injector));
    this.origin = origin;
    const panel = (attached.location.nativeElement as HTMLElement).querySelector('.ws-switcher');
    if (panel) enterMotion({ motion: 'pop', anchor: () => this.origin })(event(panel));
    ref.backdropClick().subscribe(() => this.close());
    ref.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });

    this.analytics.capture(ANALYTICS_EVENTS.ORG_SWITCHER_DIALOG_OPENED, {
      customer_count: this.customers.customers().length,
    });
  }

  close(): void {
    const ref = this.overlayRef;
    this.overlayRef = null;
    if (!ref) return;
    const panel = ref.overlayElement.querySelector('.ws-switcher');
    if (!panel) {
      ref.dispose();
      return;
    }
    const home = this.origin && isShowing(this.origin) ? this.origin : null;
    ref.detachBackdrop();
    leaveMotion({ anchor: () => home, scale: 0.96 })(event(panel, () => ref.dispose()));
  }
}
